import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import apiService from '../../context/apiService.js';
import { toast } from 'react-toastify';
import Card from '../../components/Card.jsx';
import Button from '../../components/Button.jsx';
import { Play, Lock, CheckCircle, Clock, BookOpen, Star, Users, ArrowLeft, Loader2 } from 'lucide-react';

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const courseRes = await apiService.courses.getById(id);
        if (courseRes.data.success) setCourse(courseRes.data.course);

        try {
          const enrollRes = await apiService.enrollment.checkEnrollment(id);
          setIsEnrolled(enrollRes.data.success);
        } catch { setIsEnrolled(false); }

        const lessonsRes = await apiService.lessons.getByCourse(id);
        if (lessonsRes.data.success) {
          setLessons(lessonsRes.data.lessons || []);
          if (lessonsRes.data.lessons?.length > 0) setSelectedLesson(lessonsRes.data.lessons[0]);
        }
      } catch (error) {
        toast.error('Failed to load course');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);

  const handleEnroll = async () => {
    setEnrolling(true);
    try {
      const response = await apiService.enrollment.enroll(id);
      if (response.data.success) {
        toast.success('Successfully enrolled!');
        setIsEnrolled(true);
        window.location.reload();
      }
    } catch (error) {
      toast.error('Failed to enroll');
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="w-6 h-6 animate-spin mr-2" />
      <span>Loading...</span>
    </div>
  );

  if (!course) return <div className="text-center py-12">Course not found</div>;

  return (
    <div className="space-y-6">
      <Button variant="outline" onClick={() => navigate('/student/courses')} className="flex items-center">
        <ArrowLeft size={16} className="mr-2" />Back to Courses
      </Button>

      <Card>
        <div className="flex flex-col lg:flex-row gap-6">
          <img src={course.thumbnail || 'https://via.placeholder.com/400x300'} alt={course.title} className="lg:w-80 h-48 lg:h-auto object-cover rounded-lg" />
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-secondary-900 mb-2">{course.title}</h1>
            <p className="text-secondary-600 mb-4">{course.description}</p>
            <div className="flex gap-4 mb-4 text-sm text-secondary-600">
              <span className="flex items-center"><Star size={16} className="mr-1 text-yellow-500 fill-current" /> 4.5</span>
              <span className="flex items-center"><Users size={16} className="mr-1" /> {course.studentEnroll?.length || 0} students</span>
              <span className="flex items-center"><BookOpen size={16} className="mr-1" /> {lessons.length} lessons</span>
              <span className="flex items-center"><span className="font-semibold mr-1">Category:</span> <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs">{course.category}</span></span>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-primary-600">₹{course.price}</div>
              {isEnrolled ? (
                <div className="flex items-center text-green-600"><CheckCircle size={20} className="mr-2" />Enrolled</div>
              ) : (
                <Button onClick={handleEnroll} disabled={enrolling}>{enrolling ? 'Enrolling...' : 'Enroll Now'}</Button>
              )}
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <Card.Header>
            <Card.Title>Course Content</Card.Title>
            {!isEnrolled && <Card.Description className="text-xs text-amber-600">🔓 Free lessons • Enroll for full access</Card.Description>}
          </Card.Header>
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {lessons.length === 0 ? (
              <p className="text-center text-secondary-500 py-8">No lessons available yet</p>
            ) : (
              lessons.map((lesson) => {
                const canAccess = lesson.isFree || isEnrolled;
                const isSelected = selectedLesson?._id === lesson._id;
                return (
                  <div
                    key={lesson._id}
                    onClick={() => canAccess && setSelectedLesson(lesson)}
                    className={`p-3 rounded-lg cursor-pointer transition-all ${isSelected ? 'bg-primary-50 border-2 border-primary-600' : canAccess ? 'bg-secondary-50 hover:bg-secondary-100 border-2 border-transparent' : 'bg-gray-100 border-2 border-transparent opacity-60'}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-secondary-500">Lesson {lesson.order}</span>
                          {lesson.isFree && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Free</span>}
                        </div>
                        <h4 className="font-medium text-secondary-900 text-sm line-clamp-2">{lesson.title}</h4>
                        <div className="flex items-center text-xs text-secondary-500 mt-1">
                          <Clock size={12} className="mr-1" />{lesson.duration || 0} min
                        </div>
                      </div>
                      {canAccess ? <Play size={20} className="text-primary-600" /> : <Lock size={20} className="text-secondary-400" />}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Card>

        <Card className="lg:col-span-2">
          {selectedLesson ? (
            (selectedLesson.isFree || isEnrolled) ? (
              <>
                <div className="aspect-video bg-black rounded-lg overflow-hidden">
                  <video key={selectedLesson._id} controls className="w-full h-full">
                    <source src={selectedLesson.videoUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
                <div className="p-4">
                  <h2 className="text-2xl font-bold text-secondary-900 mb-2">{selectedLesson.title}</h2>
                  <p className="text-secondary-600">{selectedLesson.description}</p>
                </div>
              </>
            ) : (
              <div className="aspect-video bg-secondary-100 rounded-lg flex flex-col items-center justify-center p-8 text-center">
                <Lock size={48} className="text-secondary-400 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-900 mb-2">This lesson is locked</h3>
                <p className="text-secondary-600 mb-4">Enroll in this course to access all lessons</p>
                <Button onClick={handleEnroll} disabled={enrolling}>{enrolling ? 'Enrolling...' : 'Enroll Now'}</Button>
              </div>
            )
          ) : (
            <div className="aspect-video bg-secondary-50 rounded-lg flex flex-col items-center justify-center p-8 text-center">
              <Play size={48} className="text-secondary-400 mb-4" />
              <h3 className="text-xl font-semibold text-secondary-900 mb-2">Select a lesson to start learning</h3>
              <p className="text-secondary-600">Choose from the lessons list on the left</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default CourseDetail;
